import { createContext, forwardRef, useContext, useRef, useState } from "react";
import { ArrowIcon, MapPinIcon, SpinnerIcon } from "../Icons";
import { EstimatedQuoteRes } from "../../types/Logistics";
import { calculatePriceRange } from "../../utils";

type QuoteContextType = {
  data: EstimatedQuoteRes;
  dialogRef: React.RefObject<HTMLDialogElement>;
  openSuccess: () => void;
  closeSuccess: () => void;
};

const EstimatedQuoteContext = createContext<QuoteContextType | null>(null);

const useEstimatedQuoteContext = () => {
  const ctx = useContext(EstimatedQuoteContext);
  if (!ctx) {
    throw new Error("EstimatedQuoteCard components must be used inside Root");
  }
  return ctx;
};

type RootProps = {
  data: EstimatedQuoteRes;
  children:
    | React.ReactNode
    | ((actions: {
        dialogRef: React.RefObject<HTMLDialogElement>;
        openSuccess: () => void;
        closeSuccess: () => void;
      }) => React.ReactNode);
};

export default function EstimatedQuoteCard({ data, children }: RootProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const openSuccess = () => dialogRef.current?.showModal();
  const closeSuccess = () => dialogRef.current?.close();

  return (
    <EstimatedQuoteContext.Provider
      value={{ data, dialogRef, openSuccess, closeSuccess }}
    >
      {typeof children === "function"
        ? children({ dialogRef, openSuccess, closeSuccess })
        : children}
    </EstimatedQuoteContext.Provider>
  );
}

EstimatedQuoteCard.Body = function ({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
      {children}
    </div>
  );
};

EstimatedQuoteCard.Header = function ({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="flex justify-between items-center pb-2 border-b border-gray-100">
      {children}
    </div>
  );
};

EstimatedQuoteCard.Title = function () {
  return (
    <h2 className="text-lg font-semibold text-gray-800">
      Estimated Freight Quote
    </h2>
  );
};

EstimatedQuoteCard.Action = function ({
  children,
}: {
  children: React.ReactNode;
}) {
  return <div className="flex items-center gap-3">{children}</div>;
};

EstimatedQuoteCard.Price = function () {
  const { data } = useEstimatedQuoteContext();
  const { quote } = data;

  return (
    <span className="text-xl font-bold text-blue-600">
      {calculatePriceRange(quote.estimated_price_usd).formatted("$")}
    </span>
  );
};

EstimatedQuoteCard.RequestBtn = function ({
  handleClick,
  isLoading,
}: {
  isLoading: boolean;
  handleClick: () => void;
}) {
  return (
    <button
      className="px-3 py-1 flex items-center gap-2 bg-custom-blue-medium text-white text-sm rounded-md disabled:bg-custom-blue-medium/70"
      onClick={handleClick}
      disabled={isLoading}
    >
      {isLoading && (
        <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-custom-blue-medium" />
      )}
      Request
    </button>
  );
};

EstimatedQuoteCard.Route = function () {
  const { data } = useEstimatedQuoteContext();
  const { request_details } = data;

  return (
    <div className="flex items-center bg-blue-50 p-3 rounded-lg">
      {/* From */}
      <div className="flex items-center gap-2">
        <MapPinIcon className="w-5 h-5 text-blue-600" />
        <div>
          <h4 className="text-xs text-gray-500">From</h4>
          <p className="font-medium text-gray-800">{request_details.from}</p>
        </div>
      </div>

      {/* Arrow */}
      <div className="flex-1 flex justify-center text-blue-500 text-xl font-semibold">
        →
      </div>

      {/* To */}
      <div className="flex items-center gap-2">
        <MapPinIcon className="w-5 h-5 text-red-500" />
        <div>
          <h4 className="text-xs text-gray-500">To</h4>
          <p className="font-medium text-gray-800">{request_details.to}</p>
        </div>
      </div>
    </div>
  );
};

EstimatedQuoteCard.Details = function () {
  const { data } = useEstimatedQuoteContext();
  const { request_details, quote } = data;

  return (
    <div className="grid grid-cols-4 gap-4 text-sm text-gray-700">
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Mode</span>
        <span className="font-medium capitalize">{request_details.type}</span>
      </div>
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Weight</span>
        <span className="font-medium">{request_details.weight}</span>
      </div>
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Transit</span>
        <span className="font-medium">{quote.estimated_transit_days} days</span>
      </div>
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Validity</span>
        <span className="font-medium">{quote.validity_days} days</span>
      </div>
    </div>
  );
};

EstimatedQuoteCard.CostBreakdown = function () {
  const [showBreakdown, setShowBreakdown] = useState(false);

  const { data } = useEstimatedQuoteContext();
  const { quote } = data;

  return (
    <div className="border-t border-gray-100 pt-2">
      <button
        type="button"
        onClick={() => setShowBreakdown(!showBreakdown)}
        className="flex justify-between w-full items-center text-gray-700 font-medium hover:text-blue-600 transition-colors"
      >
        <span>Cost Breakdown</span>

        <ArrowIcon
          className={`w-5 h-5 transform transition-transform duration-300 ${
            showBreakdown ? "rotate-180" : ""
          }`}
        />
      </button>

      <div
        className={`mt-2 grid grid-cols-2 gap-2 text-gray-700 text-sm transition-all duration-300 overflow-hidden ${
          showBreakdown ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        {Object.entries(quote.breakdown).map(([key, value]) => (
          <div
            key={key}
            className="flex justify-between bg-gray-50 px-3 py-1 rounded-lg shadow-sm"
          >
            <span className="capitalize">
              {key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            </span>
            <span className="font-medium">~${value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

EstimatedQuoteCard.SuccessDialog = forwardRef<
  HTMLDialogElement,
  { children?: React.ReactNode }
>(function SuccessDialog({ children }, ref) {
  return (
    <div>
      <dialog
        ref={ref}
        className="mt-40 rounded-2xl p-6 w-[400px] max-w-[90%] shadow-lg bg-white text-center backdrop:bg-black/40 transition-all duration-300"
      >
        {/* ✅ Success icon */}
        <div className="flex justify-center mb-4">
          <div className="bg-green-100 text-green-600 rounded-full p-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        {/* 🧾 Message */}
        <h2 className="text-lg font-semibold text-gray-800">
          Request Submitted Successfully!
        </h2>
        <p className="text-gray-600 text-sm mt-2">
          Your logistics request has been received. We’ll notify you once it’s
          processed.
        </p>

        {/* Divider */}
        <div className="w-full h-[1px] bg-gray-200 my-4"></div>

        {/* Buttons */}
        <div>{children}</div>
      </dialog>
    </div>
  );
});
